import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
    RETURNING_COOLDOWN_CHOICES, RETURNING_DAY_CHOICES, RETURNING_VISIT_CHOICES, RETURNING_WINDOW_CHOICES, ReturningRule,
    patchReturningRule, returningRule,
} from '../../../modules/vendor/settings/models/chat-settings';

type Slot = keyof ReturningRule;

/**
 * The rule for inviting a returning shopper, written as the sentence it is.
 *
 * Four numbers in four boxes read as configuration; the same four numbers
 * inside "Invite shoppers who viewed the same product 3 times, on at least 2
 * different days, within the last 14 days" read as a decision the vendor can
 * check against their own shoppers. Each box carries its unit ("3 times",
 * "2 different days") so the sentence stays grammatical in every language
 * and at every count. The sentences come from the translations with
 * [visits]-style slots, so word order is the language's own.
 */
export const ReturningRuleField = ({
    value,
    onChange,
}: {
    value: Partial<ReturningRule> | undefined;
    onChange: (rule: ReturningRule) => void;
}) => {
    const { t } = useTranslation();
    const rule = returningRule(value);

    const select = (slot: Slot, choices: readonly number[], label: (n: number) => string, aria: string) => (
        <select
            key={slot}
            id={`chat-returning-${slot}`}
            aria-label={aria}
            data-testid={`returning-rule-${slot}`}
            className="mx-1 inline-flex h-8 rounded-md border border-input bg-background px-2 align-baseline text-sm font-medium shadow-sm"
            value={rule[slot]}
            onChange={(e) => onChange(patchReturningRule(value, { [slot]: Number(e.target.value) }))}
        >
            {choices.map((n) => <option key={n} value={n}>{label(n)}</option>)}
        </select>
    );

    const slots: Record<string, ReactNode> = {
        visits: select('minVisits', RETURNING_VISIT_CHOICES, (n) => t('SHOPPER_UI.SETTINGS.RETURNING_TIMES', { count: n }), t('SHOPPER_UI.SETTINGS.RETURNING_ARIA_VISITS')),
        days: select('minDays', RETURNING_DAY_CHOICES, (n) => t('SHOPPER_UI.SETTINGS.RETURNING_DIFFERENT_DAYS', { count: n }), t('SHOPPER_UI.SETTINGS.RETURNING_ARIA_DAYS')),
        window: select('windowDays', RETURNING_WINDOW_CHOICES, (n) => t('SHOPPER_UI.SETTINGS.RETURNING_DAYS', { count: n }), t('SHOPPER_UI.SETTINGS.RETURNING_ARIA_WINDOW')),
        cooldown: select('cooldownDays', RETURNING_COOLDOWN_CHOICES, (n) => t('SHOPPER_UI.SETTINGS.RETURNING_DAYS', { count: n }), t('SHOPPER_UI.SETTINGS.RETURNING_ARIA_COOLDOWN')),
    };

    /** "Invite … [visits], on at least [days] …" → text with the boxes in place. */
    const sentence = (key: string) =>
        t(key).split(/(\[\w+\])/).map((part, i) => {
            const slot = /^\[(\w+)\]$/.exec(part)?.[1];
            return slot && slots[slot] ? <span key={i}>{slots[slot]}</span> : <span key={i}>{part}</span>;
        });

    return (
        <div className="flex flex-col gap-2" data-testid="returning-rule">
            <p className="text-xs font-medium text-muted-foreground">{t('SHOPPER_UI.SETTINGS.RETURNING_RULE_TITLE')}</p>
            <p className="text-sm leading-9" data-testid="returning-rule-sentence">{sentence('SHOPPER_UI.SETTINGS.RETURNING_RULE_SENTENCE')}</p>
            <p className="text-sm leading-9">{sentence('SHOPPER_UI.SETTINGS.RETURNING_RULE_COOLDOWN')}</p>
            <p className="text-xs text-muted-foreground">{t('SHOPPER_UI.SETTINGS.RETURNING_RULE_NOTE')}</p>
        </div>
    );
};
